import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { usePortalContainer } from '@/contexts/PhoneFrameContext';
import { createPortal } from 'react-dom';

interface ImageLightboxProps {
  src: string;
  alt?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

/**
 * Full-screen lightbox overlay with pinch-to-zoom and swipe-to-dismiss.
 * Renders via portal so it escapes parent overflow/z-index stacking.
 *
 * Uses native (non-passive) touch listeners so preventDefault() works
 * for pinch-to-zoom on iOS Safari / Capacitor WebView.
 */
export function ImageLightbox({ src, alt = '', open, onOpenChange }: ImageLightboxProps) {
  const portalContainer = usePortalContainer();
  const [zoom, setZoom] = useState<ZoomState>({ scale: 1, x: 0, y: 0 });
  const zoomRef = useRef<ZoomState>({ scale: 1, x: 0, y: 0 });
  const lastTouchRef = useRef<{ x: number; y: number; distance: number } | null>(null);
  const lastTapRef = useRef(0);
  const [dragY, setDragY] = useState(0);
  const dragYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const touchAreaRef = useRef<HTMLDivElement>(null);

  // Keep refs in sync with state
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { dragYRef.current = dragY; }, [dragY]);

  const isZoomed = zoom.scale > 1.05;
  const isZoomedRef = useRef(isZoomed);
  useEffect(() => { isZoomedRef.current = isZoomed; }, [isZoomed]);

  const resetAndClose = useCallback(() => {
    setZoom({ scale: 1, x: 0, y: 0 });
    setDragY(0);
    onOpenChange(false);
  }, [onOpenChange]);
  const resetAndCloseRef = useRef(resetAndClose);
  useEffect(() => { resetAndCloseRef.current = resetAndClose; }, [resetAndClose]);

  const getDistance = (touches: TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getMidpoint = (touches: TouchList) => {
    if (touches.length < 2) return { x: touches[0].clientX, y: touches[0].clientY };
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  };

  // Attach native non-passive touch listeners so preventDefault works
  useEffect(() => {
    if (!open) return;
    const el = touchAreaRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        lastTouchRef.current = {
          x: getMidpoint(e.touches).x,
          y: getMidpoint(e.touches).y,
          distance: getDistance(e.touches),
        };
      } else if (e.touches.length === 1) {
        lastTouchRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          distance: 0,
        };
        isDraggingRef.current = false;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      try {
        if (!lastTouchRef.current) return;

        if (e.touches.length === 2) {
          e.preventDefault();
          const newDistance = getDistance(e.touches);
          if (!newDistance || !lastTouchRef.current.distance || lastTouchRef.current.distance === 0) {
            lastTouchRef.current = {
              x: getMidpoint(e.touches).x,
              y: getMidpoint(e.touches).y,
              distance: newDistance || 0,
            };
            return;
          }
          const midpoint = getMidpoint(e.touches);
          const scaleDelta = newDistance / lastTouchRef.current.distance;
          if (!isFinite(scaleDelta) || isNaN(scaleDelta)) return;

          const savedTouch = { x: lastTouchRef.current.x, y: lastTouchRef.current.y };

          setZoom(prev => {
            const newScale = Math.min(Math.max(prev.scale * scaleDelta, 1), 5);
            if (newScale === 1) return { scale: 1, x: 0, y: 0 };
            return {
              scale: newScale,
              x: prev.x + (midpoint.x - savedTouch.x),
              y: prev.y + (midpoint.y - savedTouch.y),
            };
          });

          lastTouchRef.current = {
            x: midpoint.x,
            y: midpoint.y,
            distance: newDistance,
          };
        } else if (e.touches.length === 1) {
          const deltaX = e.touches[0].clientX - lastTouchRef.current.x;
          const deltaY = e.touches[0].clientY - lastTouchRef.current.y;
          isDraggingRef.current = true;

          if (isZoomedRef.current) {
            e.preventDefault();
            setZoom(prev => ({
              ...prev,
              x: prev.x + deltaX,
              y: prev.y + deltaY,
            }));
          } else {
            setDragY(prev => prev + deltaY);
          }

          lastTouchRef.current = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            distance: 0,
          };
        }
      } catch (err) {
        console.warn('ImageLightbox touch error:', err);
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const now = Date.now();
      if (e.touches.length === 0 && now - lastTapRef.current < 300 && !isDraggingRef.current) {
        if (isZoomedRef.current) {
          setZoom({ scale: 1, x: 0, y: 0 });
        } else {
          setZoom({ scale: 2.5, x: 0, y: 0 });
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }

      if (!isZoomedRef.current && Math.abs(dragYRef.current) > 120) {
        resetAndCloseRef.current();
        return;
      }

      if (!isZoomedRef.current) {
        setDragY(0);
      }

      if (e.touches.length < 2) {
        lastTouchRef.current = null;
      }

      if (e.touches.length === 0 && zoomRef.current.scale <= 1) {
        setZoom({ scale: 1, x: 0, y: 0 });
      }

      isDraggingRef.current = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [open]);

  const opacity = isZoomed ? 1 : Math.max(0, 1 - Math.abs(dragY) / 300);

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ backgroundColor: `rgba(0,0,0,${opacity * 0.95})` }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isZoomed) resetAndClose();
          }}
        >
          {/* Close button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={resetAndClose}
            className="absolute right-4 z-[101] w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center active:scale-95 transition-transform"
            style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
          >
            <X className="w-5 h-5 text-white" />
          </motion.button>

          {/* Image – native touch listeners via ref */}
          <div
            ref={touchAreaRef}
            className="w-full h-full flex items-center justify-center touch-none select-none"
          >
            <motion.img
              src={src}
              alt={alt}
              draggable={false}
              className="max-w-full max-h-full object-contain"
              style={{
                transform: `translateY(${dragY}px) scale(${zoom.scale}) translate(${zoom.x / zoom.scale}px, ${zoom.y / zoom.scale}px)`,
                transition: isDraggingRef.current ? 'none' : 'transform 0.2s ease-out',
              }}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
          </div>

          {/* Hint */}
          {isZoomed && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
              Double-tap to reset
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, portalContainer);
}

/**
 * Hook to manage lightbox state for convenience.
 */
export function useImageLightbox() {
  const [lightboxState, setLightboxState] = useState<{ open: boolean; src: string; alt: string }>({
    open: false,
    src: '',
    alt: '',
  });

  const openLightbox = useCallback((src: string, alt = '') => {
    setLightboxState({ open: true, src, alt });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxState(prev => ({ ...prev, open: false }));
  }, []);

  return {
    lightboxProps: {
      open: lightboxState.open,
      src: lightboxState.src,
      alt: lightboxState.alt,
      onOpenChange: (open: boolean) => {
        if (!open) closeLightbox();
      },
    },
    openLightbox,
    closeLightbox,
  };
}
